import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { environment } from '@env/environment';
import { OfflineCacheStore } from './offline-cache.store';
import { OfflineService, isNoNetworkError } from './offline.service';

/** Set on an answer that came from the device, not the server. */
export const SAVED_AT_HEADER = 'X-EZ-Saved-At';

/**
 * Keeps the last answer of every read of our API, per user, and shows it when
 * the network is gone. Registered last, so it sits next to the network: the
 * other interceptors see a saved answer as a success and do not retry or log.
 * Writes pass through untouched; offline they fail as before.
 */
@Injectable()
export class OfflineCacheInterceptor implements HttpInterceptor {
  // Not worth keeping: Ezri's answers are live, logs are fire-and-forget,
  // and the session check must reflect the server.
  private static readonly skip = ['/assistant/', '/logs/', '/auth/refresh-token', '/auth/logout'];

  constructor(private store: OfflineCacheStore, private offline: OfflineService) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const key = this.keyFor(req);
    return next.handle(req).pipe(
      tap(event => {
        if (!(event instanceof HttpResponse)) return;
        this.offline.markReachable();
        if (key && event.ok) void this.store.put(key, event.body);
      }),
      catchError((error: unknown) => {
        if (!isNoNetworkError(error)) {
          if (error instanceof HttpErrorResponse) this.offline.markReachable();
          return throwError(() => error);
        }
        this.offline.markNetworkFailed();
        if (!key) return throwError(() => error);
        return from(this.store.get(key)).pipe(
          switchMap(saved => {
            if (!saved) return throwError(() => error);
            this.offline.markShownSaved(saved.savedAt);
            return of(new HttpResponse({
              body: saved.body,
              status: 200,
              url: req.urlWithParams,
              headers: new HttpHeaders({ [SAVED_AT_HEADER]: String(saved.savedAt) }),
            }));
          })
        );
      })
    );
  }

  /** The store key for a read of our API by the signed-in user; null when it is not kept. */
  private keyFor(req: HttpRequest<unknown>): string | null {
    if (req.method !== 'GET' || req.responseType !== 'json') return null;
    if (!req.url.startsWith(environment.apiUrl)) return null;
    if (OfflineCacheInterceptor.skip.some(s => req.url.includes(s))) return null;
    const userId = OfflineCacheInterceptor.userId();
    return userId ? `${userId}|${req.urlWithParams}` : null;
  }

  private static userId(): string | null {
    try {
      return (JSON.parse(localStorage.getItem('currentUser') || 'null') as { id?: string } | null)?.id ?? null;
    } catch {
      return null;
    }
  }
}
